import type { Excursion } from "./excursion";
import type { Difficulty } from "./labeling";
import type { BinaryLabel, Label, PresetResolution } from "../types";

/**
 * Copy for the trend Loop's pre-guess barrier lines (GRA-122): the player
 * sees exactly what they are betting on before committing, so the chart
 * carries labeled barrier lines and the footer carries a one-sentence rule,
 * both stated in the active difficulty preset's own numbers.
 */

/**
 * A relative (percent-from-entry) label for a barrier line, e.g. "Upper
 * +2.0%" / "Lower -1.4%". Relative rather than absolute so the label carries
 * no price while a Round is anonymized (spec §0 "anonymization includes
 * price"): only the distance from entry, not entry itself.
 */
export function barrierLabel(
  side: "upper" | "lower",
  barrierValue: number,
  entry: number,
): string {
  const name = side === "upper" ? "Upper" : "Lower";
  return `${name} ${barrierPercent(barrierValue, entry)}`;
}

/**
 * Just the signed percent of `barrierLabel`, e.g. "+2.0%": the chip a
 * phone-width chart draws instead, where the line's color already says which
 * barrier it is and the full label crowds the candles.
 */
export function barrierPercent(barrierValue: number, entry: number): string {
  const pct = entry === 0 ? 0 : ((barrierValue - entry) / entry) * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

/**
 * The first thing a player is asked, in words that assume no trading
 * vocabulary. The chart already draws the two lines and labels
 * them; the question only has to point at them.
 */
export function barrierQuestionText(): string {
  return "Which line will price touch first?";
}

/**
 * The mechanics behind the question, in the active preset's own numbers,
 * kept behind a help control rather than led with: ATR is exactly the kind of
 * vocabulary the question above avoids, so it is explained where it appears.
 */
export function barrierRuleText({ m, nMax }: Difficulty): string {
  return `The lines sit ±${m.toFixed(1)}× ATR (average true range, a typical candle's size) from the last close. The round ends when price touches one, or after ${nMax} candles.`;
}

/** Predict's Higher/Lower text for a resolution's Label (sideways is defensive-only, see ResultBanner). */
export const PREDICT_LABEL_TEXT: Record<Label, string> = {
  up: "Higher",
  down: "Lower",
  sideways: "Higher",
};

const BARRIER_NAME: Record<PresetResolution["barrier"], string> = {
  up: "upper barrier",
  down: "lower barrier",
  time: "time cap",
};

/**
 * One-line resolution detail for a trend Round's outcome (history log,
 * GRA-123): the label, the % move, and which barrier resolved it in how many
 * candles. Mirrors ResultBanner's sentence in shorter form.
 */
export function trendResolutionDetail(resolution: PresetResolution): string {
  const move = `${resolution.pctMove >= 0 ? "+" : ""}${resolution.pctMove.toFixed(2)}%`;
  const candles = resolution.daysToResolve === 1 ? "candle" : "candles";
  return `${PREDICT_LABEL_TEXT[resolution.label]} · ${move} · resolved by ${BARRIER_NAME[resolution.barrier]} in ${resolution.daysToResolve} ${candles}`;
}

// ---------------------------------------------------------------------------
// The reveal's sentences.
//
// The Predict reveal used to report the outcome alone: which barrier won, in
// how many candles. These build the rest of what a player needs to review the
// decision they actually made, namely what they called and why, how far price
// went each way before it resolved, and one question to take into the next
// round. Nothing here claims the outcome was readable in advance.
// ---------------------------------------------------------------------------

/** A percent of entry, one decimal and always signed, with no "-0.0%". */
function signedPercent(value: number): string {
  const rounded = Number(value.toFixed(1));
  const safe = rounded === 0 ? 0 : rounded;
  return `${safe >= 0 ? "+" : ""}${safe.toFixed(1)}%`;
}

function candleCount(n: number): string {
  return `${n} ${n === 1 ? "candle" : "candles"}`;
}

/**
 * The decision restated in the player's own terms: what they called and, when
 * they left one, the reason they gave for it. First person to the player, so
 * the reveal opens on their call rather than on the market's answer.
 */
export function decisionSentence(guess: BinaryLabel, note?: string): string {
  const called = `You called ${PREDICT_LABEL_TEXT[guess]}`;
  const reason = note?.trim();
  return reason ? `${called} because "${reason}"` : `${called}.`;
}

/** What the market did, plainly, with no verdict attached to it. */
export function outcomeSentence(resolution: PresetResolution): string {
  if (resolution.barrier === "time") {
    return `Neither barrier was touched in ${candleCount(resolution.daysToResolve)}; net move was ${signedPercent(resolution.pctMove)}.`;
  }
  return `Price touched the ${BARRIER_NAME[resolution.barrier]} first, after ${candleCount(resolution.daysToResolve)}.`;
}

/**
 * The path before the resolution: how far price went the player's way, and how
 * far the other way. A call that was briefly most of the way home reads very
 * differently from one price never rewarded at all, and the old reveal made
 * those two look identical.
 */
export function pathSentence({
  towardPct,
  againstPct,
  towardFraction,
}: Excursion): string {
  const against =
    againstPct === 0
      ? "never went against you"
      : `against you to ${signedPercent(againstPct)}`;

  if (towardPct === 0) {
    return `Never went your way; ${against}.`;
  }

  const share = `${Math.round(towardFraction * 100)}% of the way there`;
  return `Went your way as far as ${signedPercent(towardPct)} (${share}), ${against}.`;
}

/** The quoted reason, short enough to sit inside a question about it. */
function shortNote(note: string): string {
  const trimmed = note.trim();
  return trimmed.length > 40 ? `${trimmed.slice(0, 40).trimEnd()}...` : trimmed;
}

/**
 * One question to carry into the next round. Phrased as a question in every
 * case: the point is to send the player back to the chart, not to tell them
 * the move was there to be read.
 *
 * The wrong-call question asks what else the Context showed rather than where
 * the reason "stopped holding", which the old wording did. One outcome cannot
 * tell a player their read was wrong, only that it did not pay this time, and
 * a question that assumes otherwise teaches hindsight.
 */
export function reviewPrompt(correct: boolean, note?: string): string {
  const reason = note?.trim();
  if (reason) {
    return correct
      ? `Would "${shortNote(reason)}" have read the same if price had gone the other way?`
      : `Look again at the last few candles before the decision: what did they show beside "${shortNote(reason)}"?`;
  }
  return correct
    ? "Next round, write down why. A right call with a reason is worth more than a right call."
    : "Next round, write one line on why before you commit. It makes results easier to learn from.";
}
